import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Loader2, Scale, Wheat } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import StagePill from "@/components/StagePill";
import VeggieIcon from "@/components/VeggieIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
    listBeds, listCrops, createCrop, updateCrop, deleteCrop,
    recordHarvest, listCropCatalog, STAGES, SOWING_TYPES, formatApiError,
} from "@/lib/api";
import { useGardens } from "@/contexts/GardenContext";
import UpgradeBanner, { upgradeMessageFrom } from "@/components/UpgradeBanner";

const blankCrop = {
    bed_id: "",
    crop_name: "",
    variety: "",
    sowing_type: "direct",
    plant_count: 1,
    sowing_date_planned: "",
    transplant_date_planned: "",
    harvest_window_start: "",
    stage: "planned",
    notes: "",
};

export default function Crops() {
    const { activeGardenId, activeGarden } = useGardens();
    const [beds, setBeds] = useState([]);
    const [crops, setCrops] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(blankCrop);
    const [harvestOpen, setHarvestOpen] = useState(false);
    const [harvestForm, setHarvestForm] = useState({ cropId: "", date: new Date().toISOString().slice(0, 10), quantity_kg: 0.1, notes: "" });
    const [upgradeMsg, setUpgradeMsg] = useState(null);

    const load = async () => {
        if (!activeGardenId) {
            setBeds([]); setCrops([]); setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const [bs, cs, cat] = await Promise.all([
                listBeds(activeGardenId),
                listCrops(activeGardenId),
                listCropCatalog(),
            ]);
            setBeds(bs);
            setCrops(cs);
            setCatalog(cat);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("crops load failed:", e?.message);
            setBeds(prev => prev || []);
            setCrops(prev => prev || []);
            setCatalog(prev => prev || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeGardenId]);

    const startCreate = () => {
        if (beds.length === 0) {
            toast.error("Add a bed first");
            return;
        }
        setEditingId(null);
        setForm({ ...blankCrop, bed_id: beds[0].id });
        setOpen(true);
    };

    const startEdit = (c) => {
        setEditingId(c.id);
        setForm({
            bed_id: c.bed_id,
            crop_name: c.crop_name,
            variety: c.variety || "",
            sowing_type: c.sowing_type,
            plant_count: c.plant_count,
            sowing_date_planned: c.sowing_date_planned || "",
            transplant_date_planned: c.transplant_date_planned || "",
            harvest_window_start: c.harvest_window_start || "",
            stage: c.stage,
            notes: c.notes || "",
        });
        setOpen(true);
    };

    const submit = async () => {
        if (!form.crop_name.trim()) { toast.error("Pick a crop"); return; }
        if (!form.bed_id) { toast.error("Pick a bed"); return; }
        if (!activeGarden) { toast.error("Select a garden first"); return; }
        try {
            const payload = { ...form };
            // Strip empty strings to null
            ["sowing_date_planned", "transplant_date_planned", "harvest_window_start"].forEach(k => {
                if (!payload[k]) payload[k] = null;
            });
            if (editingId) {
                await updateCrop(editingId, payload);
                toast.success("Crop updated");
            } else {
                await createCrop({ ...payload, garden_id: activeGarden.id });
                toast.success("Crop planted");
            }
            setOpen(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this crop?")) return;
        await deleteCrop(id);
        await load();
    };

    const startHarvest = (cropId) => {
        setHarvestForm({ cropId, date: new Date().toISOString().slice(0, 10), quantity_kg: 0.1, notes: "" });
        setHarvestOpen(true);
    };

    const submitHarvest = async () => {
        try {
            await recordHarvest(harvestForm.cropId, {
                date: harvestForm.date,
                quantity_kg: Number(harvestForm.quantity_kg),
                notes: harvestForm.notes || null,
            });
            toast.success("Harvest logged");
            setHarvestOpen(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    const bedName = (id) => beds.find(b => b.id === id)?.name || "—";

    return (
        <div className="space-y-10" data-testid="crops-page">
            {upgradeMsg && <UpgradeBanner message={upgradeMsg} />}
            <PageHeader
                overline="Crop Tracker"
                title="From seed to harvest"
                description="Each crop carries its own little story — sown date, expected window, harvests recorded."
                action={
                    <Button onClick={startCreate} data-testid="add-crop-btn" className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-6">
                        <Plus className="w-4 h-4 mr-2" /> Plant something
                    </Button>
                }
            />

            {/* Add/Edit Dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-[var(--vm-card)] border-[var(--vm-border)] max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-serif-display text-2xl">{editingId ? "Edit crop" : "New crop"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-2 max-h-[70vh] overflow-y-auto pr-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="grid gap-2">
                                <Label>Crop</Label>
                                <Select value={form.crop_name} onValueChange={v => setForm({ ...form, crop_name: v })}>
                                    <SelectTrigger data-testid="crop-name-select"><SelectValue placeholder="Pick crop" /></SelectTrigger>
                                    <SelectContent>
                                        {catalog.map(c => <SelectItem key={c.key} value={c.common_name}>{c.common_name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-2">
                                <Label>Variety</Label>
                                <Input data-testid="crop-variety-input" value={form.variety} onChange={e => setForm({ ...form, variety: e.target.value })} placeholder="e.g. Cherokee Purple" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="grid gap-2">
                                <Label>Bed</Label>
                                <Select value={form.bed_id} onValueChange={v => setForm({ ...form, bed_id: v })}>
                                    <SelectTrigger data-testid="crop-bed-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {beds.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-2">
                                <Label>Plants</Label>
                                <Input type="number" min="1" data-testid="crop-count-input" value={form.plant_count} onChange={e => setForm({ ...form, plant_count: Number(e.target.value) })} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="grid gap-2">
                                <Label>Sowing</Label>
                                <Select value={form.sowing_type} onValueChange={v => setForm({ ...form, sowing_type: v })}>
                                    <SelectTrigger data-testid="crop-sowing-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {SOWING_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-2">
                                <Label>Stage</Label>
                                <Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v })}>
                                    <SelectTrigger data-testid="crop-stage-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="grid gap-2">
                                <Label>Sow date</Label>
                                <Input type="date" data-testid="crop-sow-date" value={form.sowing_date_planned} onChange={e => setForm({ ...form, sowing_date_planned: e.target.value })} />
                            </div>
                            <div className="grid gap-2">
                                <Label>Transplant</Label>
                                <Input type="date" data-testid="crop-transplant-date" value={form.transplant_date_planned} onChange={e => setForm({ ...form, transplant_date_planned: e.target.value })} />
                            </div>
                            <div className="grid gap-2">
                                <Label>Harvest from</Label>
                                <Input type="date" data-testid="crop-harvest-date" value={form.harvest_window_start} onChange={e => setForm({ ...form, harvest_window_start: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid gap-2">
                            <Label>Notes</Label>
                            <Textarea data-testid="crop-notes-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={submit} data-testid="crop-save-btn" className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]">
                            {editingId ? "Save" : "Plant it"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Harvest Dialog */}
            <Dialog open={harvestOpen} onOpenChange={setHarvestOpen}>
                <DialogContent className="bg-[var(--vm-card)] border-[var(--vm-border)]">
                    <DialogHeader>
                        <DialogTitle className="font-serif-display text-2xl">Log harvest</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-2">
                        <div className="grid gap-2">
                            <Label>Date</Label>
                            <Input type="date" data-testid="harvest-date-input" value={harvestForm.date} onChange={e => setHarvestForm({ ...harvestForm, date: e.target.value })} />
                        </div>
                        <div className="grid gap-2">
                            <Label>Quantity (kg)</Label>
                            <Input type="number" step="0.01" min="0" data-testid="harvest-qty-input" value={harvestForm.quantity_kg} onChange={e => setHarvestForm({ ...harvestForm, quantity_kg: e.target.value })} />
                        </div>
                        <div className="grid gap-2">
                            <Label>Notes</Label>
                            <Textarea data-testid="harvest-notes-input" value={harvestForm.notes} onChange={e => setHarvestForm({ ...harvestForm, notes: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setHarvestOpen(false)}>Cancel</Button>
                        <Button onClick={submitHarvest} data-testid="harvest-save-btn" className="rounded-full bg-[var(--vm-secondary)] hover:bg-[var(--vm-secondary-hover)] text-[var(--vm-bg)]">
                            Log harvest
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {loading ? (
                <div className="text-[var(--vm-text-muted)] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : crops.length === 0 ? (
                <div className="journal-card p-12 text-center">
                    <Wheat className="w-10 h-10 mx-auto text-[var(--vm-sage)] mb-3" strokeWidth={1.5} />
                    <h3 className="font-serif-display text-2xl text-[var(--vm-text)] mb-2">No crops planted</h3>
                    <p className="text-[var(--vm-text-secondary)] mb-5">Add your first crop to begin tracking from seed to harvest.</p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {crops.map(c => {
                        const harvested = (c.harvests || []).reduce((s, h) => s + (h.quantity_kg || 0), 0);
                        return (
                            <div key={c.id} className="journal-card p-5" data-testid={`crop-row-${c.id}`}>
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                    <div className="flex-1 flex items-start gap-4">
                                        <div className="w-14 h-14 rounded-lg bg-[var(--vm-input)]/50 border border-[var(--vm-border)] flex items-center justify-center shrink-0">
                                            <VeggieIcon cropName={c.crop_name} stage={c.stage} size={42} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">{c.crop_name}</h3>
                                                {c.variety && <span className="text-sm text-[var(--vm-text-muted)] italic">'{c.variety}'</span>}
                                                <StagePill stage={c.stage} />
                                            </div>
                                            <div className="text-sm text-[var(--vm-text-secondary)] mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                                                <span>{c.plant_count} plants in {bedName(c.bed_id)}</span>
                                                {c.sowing_date_planned && <span>Sow {c.sowing_date_planned}</span>}
                                                {c.harvest_window_start && <span>Harvest from {c.harvest_window_start}</span>}
                                                {(c.expected_yield_low_kg !== null && c.expected_yield_high_kg !== null) && (
                                                    <span className="flex items-center gap-1"><Scale className="w-3 h-3" /> {c.expected_yield_low_kg}–{c.expected_yield_high_kg} kg</span>
                                                )}
                                                {harvested > 0 && <span className="text-[var(--vm-secondary)] font-medium">{harvested.toFixed(2)} kg harvested</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <Button size="sm" variant="ghost" onClick={() => startHarvest(c.id)} data-testid={`harvest-${c.id}`} className="text-[var(--vm-secondary)]">
                                            <Wheat className="w-4 h-4 mr-1" /> Harvest
                                        </Button>
                                        <Button size="icon" variant="ghost" onClick={() => startEdit(c)} data-testid={`edit-crop-${c.id}`}>
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`delete-crop-${c.id}`}>
                                            <Trash2 className="w-4 h-4 text-[var(--vm-secondary)]" />
                                        </Button>
                                    </div>
                                </div>
                                {c.notes && <div className="mt-3 text-sm italic text-[var(--vm-text-secondary)]">{c.notes}</div>}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
